import { useState } from 'react';
import type { SessionConnectionEntry, ConnectionState, PlatformKey } from '@clarity/contract';
import { PLATFORMS } from '@clarity/contract';
import { useConnections } from '../../queries/index.js';
import { useI18n } from '../../lib/i18n.js';
import { Modal } from '../Modal.js';
import { dateTime } from '../format.js';
import { IconConnections } from '../../components/icons.js';

/**
 * Admin-only platform-connections surface (clarity-platform-abstraction task
 * 5.2; Req 5.1, 5.2, 5.5, 5.6), built against the signed-off mock
 * `brand/platform-connections-mock.html` in the teal-glass world.
 *
 * ── What it shows ──
 * One card per LIVE platform returned by {@link useConnections} (`GET
 * /admin/connections` → `ConnectionStatus[]`). Each card carries the platform's
 * display name (from `PLATFORMS[key].name`), the shared auth-model label
 * "Browser session · Chrome" (every live platform authenticates the SAME way),
 * a live state pill mapping `connected | expired | disconnected` — plus a
 * `checking` state while the first probe runs — to the mock's hue classes, and
 * the last-checked time formatted from the ISO `checkedAt`.
 *
 * ── The one shared re-auth flow (Req 5.2) ──
 * A SINGLE modal, parameterized by platform. It is the "log in to the linked
 * Chrome; recovery is automatic" flow — there is NO token/cookie/paste UI, by
 * design: Clarity reads the live session, nothing is stored or entered. Opened
 * by "Reconnect" on an expired/disconnected card and "How it works" on every
 * card.
 *
 * ── Reserved platforms (Req 5.5) ──
 * Hidden entirely — the API only returns live platforms (reserved excluded
 * server-side via `livePlatforms()`), and we render strictly from that array,
 * so no placeholder card ever appears for a reserved key.
 *
 * ── Coverage expander decision (mock's `.cov-toggle`) ──
 * The mock shows a collapsed "Coverage · N branches" expander. The status API
 * carries ONLY `{ platform, status, checkedAt }` — no store/branch coverage —
 * so there is no real data to drive it with. Per the task's guidance we do NOT
 * invent a fake branch list in production: the expander is omitted gracefully
 * while no coverage source exists. The card's visual structure is otherwise
 * faithful to the mock, so wiring a real coverage source later (a count +
 * branch chips) is a drop-in addition in the card body.
 *
 * Admin-only: the route guard in `AppRoutes` is the real gate (a client never
 * reaches it) and the hidden "Manage" nav entry is the convenience; the server
 * independently enforces admin-only on `GET /admin/connections`. Craft floor
 * (Req 5.6): teal-glass tokens, `--font-num` for the timestamp, RTL-safe via
 * logical properties, WCAG 2.1 AA (state spelled out in text, labelled modal,
 * `role="status"`/`role="alert"`), reduced-motion honored by the shared pulse
 * keyframes gated in `app.css`.
 */
export function Connections() {
  const { t } = useI18n();
  const query = useConnections();
  // The one shared re-auth modal, keyed by the platform it targets (null = closed).
  const [reauth, setReauth] = useState<PlatformKey | null>(null);

  const rows: SessionConnectionEntry[] =
    query.data?.status === 200
      ? query.data.body.filter((e): e is SessionConnectionEntry => e.authModel === 'session')
      : [];
  const hasData = query.data?.status === 200;
  // Only surface the error banner when there's NO data to show. The query polls
  // (`refetchInterval`), so a transient refetch failure must not blank out or
  // overlay an error on already-loaded cards — last-known status stays visible.
  const showError = query.isError && !hasData;

  return (
    <section className="conn-page" aria-label={t('connections.title')}>
      <div className="page-head conn-page__head">
        <div>
          <span className="admin-tag">
            <IconShield className="admin-tag__ico" />
            {t('connections.adminOnly')}
          </span>
          <h2>{t('connections.title')}</h2>
          <p className="sub">{t('connections.lede')}</p>
        </div>
      </div>

      {/* The one shared model note — every platform uses the same Chrome model. */}
      <div className="card conn-note" role="note">
        <IconInfo className="conn-note__ico" />
        <p className="conn-note__text">{t('connections.modelNote')}</p>
      </div>

      {/* Loading: the first probe is running — a real state, not a blank. */}
      {query.isPending && (
        <p role="status" className="muted conn-page__status">
          {t('connections.loading')}
        </p>
      )}

      {/* Error: the admin read failed with no data to fall back on (transport,
          or 403 for a non-admin reaching it). A transient refetch error over
          existing data never reaches here (see `showError`). */}
      {showError && (
        <p role="alert" className="error conn-page__status">
          {t('connections.error')}
        </p>
      )}

      {/* Empty: the read succeeded but no live platform is wired yet. */}
      {hasData && rows.length === 0 && (
        <div className="card empty">
          <IconConnections className="empty__glyph" aria-hidden="true" />
          <div className="empty__title">{t('connections.empty.title')}</div>
          <p className="empty__sub">{t('connections.empty.sub')}</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="conns" aria-label={t('connections.title')}>
          {rows.map((row) => (
            <ConnectionCard key={row.platform} status={row} onReauth={() => setReauth(row.platform)} />
          ))}
        </div>
      )}

      {reauth && <ReauthModal platform={reauth} onClose={() => setReauth(null)} />}
    </section>
  );
}

/** The state → mock hue-class map, shared by the card pill and the detail copy. */
const STATE_CLASS: Record<ConnectionState, string> = {
  connected: 'connected',
  expired: 'expired',
  disconnected: 'disconnected',
};

/**
 * One live platform's card (mock's `render()` per-card structure): the logo tile
 * + name + auth-model label, the live state pill, the last-checked time, and the
 * actions — "Reconnect" (only when expired/disconnected) + "How it works" (all
 * cards), both opening the one shared re-auth modal. The coverage expander is
 * intentionally omitted until a real coverage source exists (see the file note).
 */
function ConnectionCard({
  status,
  onReauth,
}: {
  status: SessionConnectionEntry;
  onReauth: () => void;
}) {
  const { t, localeTag } = useI18n();
  const key = status.platform;
  const descriptor = PLATFORMS[key];
  const name = descriptor.name;
  const needsReconnect = status.status === 'expired' || status.status === 'disconnected';

  return (
    <div className="card conn-card">
      <div className="conn-card__head">
        <div className={`conn-logo conn-logo--${key}`} aria-hidden="true">
          {name.charAt(0).toUpperCase()}
        </div>
        <div className="conn-card__ident">
          <div className="conn-card__name">{name}</div>
          <div className="conn-card__auth">{t('connections.authModel')}</div>
        </div>
        <div className="conn-card__state">
          <StatePill state={status.status} />
        </div>
      </div>

      <div className="conn-card__body">
        <p className="conn-card__detail">{t(stateDetailKey(status.status))}</p>
        <div className="conn-card__meta">
          <span>
            {t('connections.checked')}{' '}
            <b className="num" title={new Date(status.checkedAt).toLocaleString(localeTag)}>
              {dateTime(status.checkedAt)}
            </b>
          </span>
        </div>
      </div>

      <div className="conn-card__actions">
        {needsReconnect && (
          <button type="button" className="primary sm" onClick={onReauth}>
            {t('connections.reconnect')}
          </button>
        )}
        <button
          type="button"
          className="ghost sm"
          onClick={onReauth}
          aria-label={t('connections.howItWorks.aria', { platform: name })}
        >
          {t('connections.howItWorks')}
        </button>
      </div>
    </div>
  );
}

/**
 * The live state pill. The state is spelled out in TEXT (not colour alone —
 * WCAG AA) and the hue comes from the shared state tokens via the mock's
 * `.pill` modifier. A `connected` dot pulses (reduced-motion zeroes it); the
 * query is never pending here (the page shows its own loading state), so the
 * three terminal states are all we render.
 */
function StatePill({ state }: { state: ConnectionState }) {
  const { t } = useI18n();
  const label = t(`connections.state.${state}` as 'connections.state.connected');
  return (
    <span className={`pill pill--${STATE_CLASS[state]}`} role="status" aria-label={t('connections.state.aria', { label })}>
      <span className="pill__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

/** The per-state detail sentence under the card head (mock's `detail`). */
function stateDetailKey(state: ConnectionState): 'connections.detail.connected' {
  return `connections.detail.${state}` as 'connections.detail.connected';
}

/**
 * The ONE shared re-auth flow, parameterized by platform (mock's `#m-reauth`).
 * Reuses the app's accessible {@link Modal} (focus trap, Escape, backdrop) and
 * fills its body with the mock's lead + numbered steps + the "recovery is
 * automatic" callout + a close/open-Chrome footer. There is deliberately NO
 * token/cookie/paste field anywhere (Req 5.2) — the whole point is that a human
 * re-logs-in in the linked Chrome and the next probe flips the state back.
 */
function ReauthModal({ platform, onClose }: { platform: PlatformKey; onClose: () => void }) {
  const { t } = useI18n();
  const name = PLATFORMS[platform].name;

  return (
    <Modal
      className="modal conn-reauth"
      title={
        <span className="conn-reauth__title">
          <span className={`conn-logo conn-logo--${platform} conn-logo--sm`} aria-hidden="true">
            {name.charAt(0).toUpperCase()}
          </span>
          {t('connections.reauth.title', { platform: name })}
        </span>
      }
      onClose={onClose}
    >
      <p>{t('connections.reauth.lead', { platform: name })}</p>
      <ol className="conn-reauth__steps">
        <li>{t('connections.reauth.step1')}</li>
        <li>{t('connections.reauth.step2', { platform: name })}</li>
        <li>{t('connections.reauth.step3')}</li>
      </ol>
      <div className="callout conn-reauth__callout">
        <IconInfo className="callout__ico" />
        <span>{t('connections.reauth.recovery')}</span>
      </div>
      <div className="modal__actions conn-reauth__foot">
        <button type="button" className="ghost" onClick={onClose}>
          {t('connections.reauth.close')}
        </button>
        <button type="button" className="primary" onClick={onClose}>
          {t('connections.reauth.openChrome')}
        </button>
      </div>
    </Modal>
  );
}

/** A filled shield glyph for the "Admin only" tag (matches the mock's SVG). */
function IconShield({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

/** A circled-i info glyph for the model note + the recovery callout. */
function IconInfo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}
